import { Link, useLocation } from "react-router-dom";
import { useConvexAuth } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { LogOut, ExternalLink, ChevronDown } from "lucide-react";

export function Header() {
  const { isAuthenticated } = useConvexAuth();
  const { signOut } = useAuthActions();
  const location = useLocation();

  const navLinks = [
    { to: "/docs", label: "Docs" },
    { to: "/playground", label: "Playground" },
    { to: "/blog", label: "Blog" },
    { to: "/pricing", label: "Pricing" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 border-b border-white/[0.06] bg-[#0a0a0b]/80 backdrop-blur-xl">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="relative">
            <img 
              src="/logo.svg" 
              alt="StarSinger" 
              className="w-8 h-8 rounded-lg"
            />
            <div className="absolute inset-0 rounded-lg bg-amber-500/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-semibold text-white text-base tracking-tight">
              StarSinger
            </span>
            <span className="text-xs font-medium text-amber-400/80 px-1.5 py-0.5 rounded bg-amber-500/10">
              MCP
            </span>
          </div>
        </Link>

        {/* Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${
                isActive(link.to)
                  ? "text-white bg-white/[0.06]"
                  : "text-white/50 hover:text-white hover:bg-white/[0.04]"
              }`}
            >
              {link.label}
            </Link>
          ))}
          
          {/* External Link */}
          <a
            href="https://starsinger.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-white/50 hover:text-white transition-colors"
          >
            StarSinger.ai
            <ExternalLink className="w-3 h-3" />
          </a>
        </nav>

        {/* Auth Actions */}
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <Link
                to="/dashboard"
                className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${
                  isActive("/dashboard")
                    ? "text-white bg-white/[0.06]"
                    : "text-white/50 hover:text-white hover:bg-white/[0.04]"
                }`}
              >
                Dashboard
              </Link>
              <button
                onClick={() => signOut()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white/50 hover:text-white hover:bg-white/[0.04] rounded-lg transition-all duration-200"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="btn btn-primary py-2 px-4 text-sm"
            >
              Sign in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
